// Mock analysis runs for working on the Dashboard without waiting for a real
// pipeline run.
//
// OFF unless explicitly switched on, and never on in a production build:
//   enable :  <app-url>/RSA-Analysis?mockRuns=1
//   disable:  <app-url>/RSA-Analysis?mockRuns=0
// The choice sticks in sessionStorage so it survives navigation inside the tab
// and dies with it.
//
// Mock runs are defined in the RAW TASK shape the API returns and pushed through
// the real transformTaskForList(), so they can't drift from what the dashboard
// actually receives. Every run_id is prefixed `mock-` so nothing can be confused
// with a real run in logs or Step Functions links.
import { transformTaskForList } from './progressDataTransformer';

const FLAG_KEY = 'rsaMockRuns';

/** Production builds never serve mock data, whatever the URL says. */
const isMockAllowed = () => process.env.REACT_APP_DEPLOY_TARGET !== 'production';

// Capture ?mockRuns=… at MODULE LOAD, not when the dashboard mounts.
// App.js bounces unauthenticated/not-yet-restored users off protected routes
// with `window.location.href = origin`, which throws away the path AND the query
// string. If we only read the param when RSAAnalysis renders, a redirect eats
// the flag before it is ever seen. This module is imported at bundle load, so
// the value is safely persisted first.
(function captureFlagEarly() {
  if (!isMockAllowed() || typeof window === 'undefined') return;
  try {
    const param = new URLSearchParams(window.location.search).get('mockRuns');
    if (param === '1' || param === 'true') {
      localStorage.setItem(FLAG_KEY, '1');
    } else if (param === '0' || param === 'false') {
      localStorage.removeItem(FLAG_KEY);
      sessionStorage.removeItem(FLAG_KEY);
    }
  } catch (e) { /* storage unavailable — fall back to per-render read */ }
})();

export function isMockRunsEnabled() {
  if (!isMockAllowed() || typeof window === 'undefined') return false;
  try {
    // Query param wins and is sticky. localStorage (not session) so it survives
    // dev-server restarts and full reloads while working on the dashboard.
    const param = new URLSearchParams(window.location.search).get('mockRuns');
    if (param === '1' || param === 'true') {
      localStorage.setItem(FLAG_KEY, '1');
      return true;
    }
    if (param === '0' || param === 'false') {
      localStorage.removeItem(FLAG_KEY);
      sessionStorage.removeItem(FLAG_KEY);
      return false;
    }
    // Also honour a plain console toggle:
    //   localStorage.setItem('rsaMockRuns','1')  then reload
    return localStorage.getItem(FLAG_KEY) === '1'
      || sessionStorage.getItem(FLAG_KEY) === '1';
  } catch (e) {
    return false;
  }
}

const minutesAgo = (m) => new Date(Date.now() - m * 60 * 1000).toISOString();

const svc = (status, progress, message) => ({
  status,
  progress,
  message,
});

/**
 * Raw task fixtures covering the states the dashboard renders differently:
 * running (mid-flight, mixed service states), completed, failed, and pending.
 */
function buildMockTasks(projectId, userEmail) {
  const pid = projectId != null ? String(projectId) : '2466';
  const email = userEmail || 'demo@example.com';
  const base = {
    project_id: pid,
    source_project_id: pid,
    mapped_project_id: pid,
    project_type: 'RSA-EXISTING',
    project_name: `Mock Project ${pid}`,
    user_email: email,
    folder_id: 'mock-folder-id',
    source: 'mock',
  };

  return [
    // 1. In-flight run — the richest view: some services done, one running, rest queued.
    {
      ...base,
      run_id: 'mock-run-running-0001',
      run_key: 'mock-run-running-0001',
      full_run_id: `${pid}/mock-run-running-0001`,
      task_id: 'mock-run-running-0001',
      status: 'processing',
      overall_progress: 46,
      created_at: minutesAgo(38),
      updated_at: minutesAgo(1),
      services: [
        'tiling', 'csf', 'road_detection', 'car_removal',
        'ssd_classification', 'shapefile', 'automatic_reports',
      ],
      services_progress: {
        tiling: svc('completed', 100, 'Tiling complete — 412 tiles'),
        csf: svc('completed', 100, 'Ground classification complete'),
        road_detection: svc('processing', 63, 'Classifying road surface (chunk 21/33)'),
        car_removal: svc('pending', 0, 'Waiting for road detection'),
        ssd_classification: svc('pending', 0, 'Queued'),
        shapefile: svc('pending', 0, 'Queued'),
        automatic_reports: svc('pending', 0, 'Queued'),
      },
      metadata: { project_type: 'RSA-EXISTING', engine: 'v2' },
    },

    // 2. Completed run.
    {
      ...base,
      run_id: 'mock-run-complete-0002',
      run_key: 'mock-run-complete-0002',
      full_run_id: `${pid}/mock-run-complete-0002`,
      task_id: 'mock-run-complete-0002',
      status: 'completed',
      overall_progress: 100,
      created_at: minutesAgo(60 * 26),
      updated_at: minutesAgo(60 * 24),
      services: [
        'tiling', 'csf', 'road_detection', 'car_removal',
        'ssd_classification', 'shapefile', 'automatic_reports',
      ],
      services_progress: {
        tiling: svc('completed', 100, 'Tiling complete — 388 tiles'),
        csf: svc('completed', 100, 'Ground classification complete'),
        road_detection: svc('completed', 100, 'Road surface classified'),
        car_removal: svc('completed', 100, 'Vehicles removed'),
        ssd_classification: svc('completed', 100, 'Sight distance computed'),
        shapefile: svc('completed', 100, 'Shapefiles written'),
        automatic_reports: svc('completed', 100, 'Reports generated'),
      },
      metadata: { project_type: 'RSA-EXISTING', engine: 'v2' },
    },

    // 3. Failed run — exercises the error styling and the logs links.
    {
      ...base,
      run_id: 'mock-run-failed-0003',
      run_key: 'mock-run-failed-0003',
      full_run_id: `${pid}/mock-run-failed-0003`,
      task_id: 'mock-run-failed-0003',
      status: 'failed',
      overall_progress: 28,
      created_at: minutesAgo(60 * 50),
      updated_at: minutesAgo(60 * 49),
      services: ['tiling', 'csf', 'road_detection'],
      services_progress: {
        tiling: svc('completed', 100, 'Tiling complete — 1,204 tiles'),
        csf: svc('failed', 34, 'DataLimitExceeded at merge step'),
        road_detection: svc('pending', 0, 'Not started'),
      },
      metadata: { project_type: 'RSA-EXISTING', engine: 'v2' },
    },

    // 4. Just-submitted run — nothing has started yet.
    {
      ...base,
      run_id: 'mock-run-pending-0004',
      run_key: 'mock-run-pending-0004',
      full_run_id: `${pid}/mock-run-pending-0004`,
      task_id: 'mock-run-pending-0004',
      status: 'pending',
      overall_progress: 0,
      created_at: minutesAgo(2),
      updated_at: minutesAgo(2),
      services: ['tiling', 'csf', 'road_detection', 'car_removal'],
      services_progress: {
        tiling: svc('pending', 0, 'Queued'),
        csf: svc('pending', 0, 'Queued'),
        road_detection: svc('pending', 0, 'Queued'),
        car_removal: svc('pending', 0, 'Queued'),
      },
      metadata: { project_type: 'RSA-EXISTING', engine: 'v2' },
    },
  ];
}

/**
 * Progress payloads keyed by run_id, matching what progressService returns.
 *
 * The dashboard reads per-service detail ONLY from `progress.services` — it does
 * not fall back to the run's own services_progress. Without an entry here the
 * step tiles render with no sub-stages, so the phase inspectors (which require
 * sub_stages.length > 0) never appear and the tiles look empty.
 */
export function getMockRunProgressMap(projectId, userEmail) {
  if (!isMockRunsEnabled()) return {};
  try {
    const map = {};
    buildMockTasks(projectId, userEmail).forEach((task) => {
      const services = task.services_progress || {};
      const names = Object.keys(services);
      map[task.run_id] = {
        run_id: task.run_id,
        status: task.status,
        services,
        overall_progress: task.overall_progress,
        total_services: names.length,
        completed_services: names.filter(
          (n) => (services[n].progress || 0) >= 100 || services[n].status === 'completed',
        ).length,
      };
    });
    return map;
  } catch (e) {
    console.warn('mockRuns: could not build progress fixtures', e);
    return {};
  }
}

/** Dashboard-ready mock runs, in the same shape the API path produces. */
export function getMockRuns(projectId, userEmail) {
  if (!isMockRunsEnabled()) return [];
  try {
    const runs = buildMockTasks(projectId, userEmail).map(transformTaskForList);
    // Loud on purpose: makes it obvious the list is fixtures, not real runs,
    // and confirms the flag took effect when nothing shows up.
    console.info(
      `%c[mockRuns] injecting ${runs.length} FAKE runs for project ${projectId ?? '(none)'} — disable with ?mockRuns=0`,
      'color:#ff8a33;font-weight:bold',
    );
    return runs;
  } catch (e) {
    console.warn('mockRuns: could not build fixtures', e);
    return [];
  }
}

export default getMockRuns;
